import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useCurrency } from "@/context/CurrencyContext";
import { ArrowRight, CalendarDays, Clock, Play, Sparkles, Users } from "lucide-react";

export default function AdultsHome() {
  const [flagship, setFlagship] = useState(null);
  const { currency, format } = useCurrency();

  useEffect(() => {
    api.get("/catalog/flagship").then((r) => setFlagship(r.data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <Navbar variant="adult" />
      <section className="relative grid-bg-adult">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="max-w-7xl mx-auto px-6 py-24 relative">
          <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono mb-6">$ ~/adults</div>
          <h1 className="font-adult-heading text-5xl md:text-6xl font-semibold tracking-tight leading-[1.02] max-w-4xl">
            A focused place to become the developer <span className="font-adult-serif italic text-amber-400">you keep meaning to be.</span>
          </h1>
          <p className="mt-6 text-zinc-400 max-w-2xl text-lg leading-relaxed">
            One flagship live cohort every 4 months. Recorded mini-courses are on the way.
          </p>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/flagship" data-testid="adults-cta-flagship" className="inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
              Weekend Bootcamp <ArrowRight className="w-4 h-4" />
            </Link>
            <span data-testid="adults-cta-courses" className="inline-flex items-center gap-2 rounded-full border border-zinc-800 px-6 py-3 text-sm text-zinc-500">
              <Clock className="w-4 h-4" /> Mini courses · coming soon
            </span>
          </div>
        </div>
      </section>

      {/* Flagship spotlight */}
      {flagship && (
        <section className="max-w-7xl mx-auto px-6 py-16">
          <div className="rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/5 to-violet-500/5 p-8 md:p-12 relative overflow-hidden">
            <div className="grid md:grid-cols-2 gap-10 items-center relative">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-amber-500/10 border border-amber-500/30 px-3 py-1 text-xs uppercase tracking-[0.2em] font-adult-mono text-amber-300">
                  <Sparkles className="w-3 h-3"/>Flagship program
                </div>
                <h2 className="mt-6 font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight leading-tight">{flagship.title}</h2>
                <p className="mt-4 text-zinc-300 leading-relaxed max-w-lg">{flagship.tagline}</p>
                <div className="mt-6 flex items-center gap-6 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Fee</div>
                    <div className="font-adult-mono text-3xl font-semibold text-amber-400 mt-1">{format(flagship[`price_${currency}`])}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Duration</div>
                    <div className="mt-1 text-lg">{flagship.duration_weeks} weeks · {flagship.days}</div>
                  </div>
                </div>
                <Link to="/flagship" data-testid="adults-flagship-view" className="mt-8 inline-flex items-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
                  View batches & syllabus <ArrowRight className="w-4 h-4"/>
                </Link>
              </div>
              <div className="relative">
                <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Data analytics dashboard with charts" className="rounded-2xl border border-zinc-800 aspect-[4/3] object-cover w-full" />
                <div className="absolute -bottom-4 -left-4 rounded-xl bg-zinc-950 border border-zinc-800 px-4 py-3 glow-violet">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-adult-mono">Next batch</div>
                  <div className="text-sm mt-1 flex items-center gap-2"><CalendarDays className="w-3.5 h-3.5 text-amber-400"/>{flagship.cohorts?.[0] ? new Date(flagship.cohorts[0].start_date_iso).toLocaleDateString(undefined,{month:"short", day:"numeric", year:"numeric"}) : "TBA"}</div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Mini courses */}
      <section className="max-w-7xl mx-auto px-6 py-14">
        <div data-testid="adults-courses-coming-soon" className="rounded-3xl border border-dashed border-zinc-700 bg-zinc-950/60 p-8 md:p-12 grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-500/10 border border-violet-500/30 px-3 py-1 text-xs uppercase tracking-[0.2em] font-adult-mono text-violet-300">
              <Clock className="w-3 h-3" /> Coming soon
            </div>
            <h2 className="mt-5 font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight">Recorded mini courses are on the way.</h2>
            <p className="mt-3 text-zinc-400 leading-relaxed max-w-xl">Snack-sized, ship-focused courses in SQL, Python and Power BI are being polished. Until then, learn live in the Weekend Bootcamp or a workshop with Sankalp.</p>
          </div>
          <div className="md:col-span-4 flex flex-col sm:flex-row md:flex-col gap-3 md:items-end">
            <Link to="/flagship" data-testid="adults-coming-soon-bootcamp" className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-semibold px-6 py-3 text-sm glow-amber transition-smooth">
              Weekend Bootcamp <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/meet-sankalp" data-testid="adults-coming-soon-workshops" className="inline-flex items-center justify-center gap-2 rounded-full border border-zinc-800 hover:border-zinc-600 px-6 py-3 text-sm text-zinc-200 transition-smooth">
              Live workshops →
            </Link>
          </div>
        </div>
      </section>

      {/* Why Sankalp */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: <Play className="w-5 h-5"/>, title: "Watch anywhere", body: "Progress tracked. Notes attached. Resume where you left off." },
            { icon: <Users className="w-5 h-5"/>, title: "Small live cohorts", body: "Every batch capped so questions actually get answered." },
            { icon: <Clock className="w-5 h-5"/>, title: "Career-first", body: "The flagship includes mock interviews and portfolio reviews." },
          ].map((f) => (
            <div key={f.title} className="rounded-2xl border border-zinc-800 p-8">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-300 mb-6">{f.icon}</div>
              <h3 className="font-adult-heading text-xl font-semibold tracking-tight">{f.title}</h3>
              <p className="mt-2 text-zinc-400 text-sm leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      <Footer variant="adult" />
    </div>
  );
}
